/**
 * useMovementEffects — Side-effect handler for FSM state transitions.
 *
 * Single useEffect watches prevState → currentState transitions
 * and triggers the appropriate side effects (audio, modals, recording).
 *
 * The FSM itself stays pure. All imperative side effects live here.
 *
 * actions shape:
 *   playGeneralAudio(audioKey)         - Play audio by key (priority 1/2)
 *   playAudioWithRetry(url, opts)      - Play audio URL with retry
 *   stopPlayingAudio()                 - Stop current audio
 *   getStartInstructionAudioUrl()      - Get START_INSTRUCTION audio URL
 *   getStartingMovementAudioUrl()      - Get STARTING_MOVEMENT audio URL
 *   onInstructionAudioComplete()       - Mark instruction audio as finished
 *   showCountdown()                    - Show countdown UI
 *   hideCountdown()                    - Hide countdown UI
 *   startRecording(duration)           - Start video recording
 *   stopRecording()                    - Stop video recording
 *   stopMultiPersonDetection()         - Stop multi-person detection interval
 *   playMultiPersonWarning()           - Play multi-person warning audio
 *   resetMovementLevelState()          - Reset per-movement state
 *   processRecordingResult()           - Handle recording completion
 *   enableFeedbackAudio()              - Enable real-time feedback audio
 *   getAudioManager()                  - Get audio manager instance
 */
import { useEffect, useRef } from 'react';
import { MovementState, MovementEvent } from './movementFSMConstants';
import CONSTANT from "../../../../constants/Constant";

function enterKey(state) {
  return `enter:${state}`;
}

function exitKey(state) {
  return `exit:${state}`;
}

export default function useMovementEffects(fsm, actions) {
  const {
    currentState,
    prevState,
    context,
    send,
  } = fsm;

  // Track pending async operations for cancellation
  const pendingAudioRef = useRef(null);
  const cancelledRef = useRef(false);
  // Track exercise changes during self-loops (same state, different exercise)
  const prevSelectionGenRef = useRef(0);
  // Generation counter to cancel stale playAndTransition when exercise changes
  const generationRef = useRef(0);

  // Cancel stale async operations on state change
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, [currentState]);

  useEffect(() => {
    const selectionGen = context._exerciseSelectionGen || 0;
    const exerciseChanged = selectionGen !== prevSelectionGenRef.current;
    prevSelectionGenRef.current = selectionGen;

    if (currentState === prevState && !exerciseChanged) return;
    if (!actions) return;

    const enter = enterKey(currentState);
    const exit = exitKey(prevState);

    // ── EXIT EFFECTS ──

    if (exit === exitKey(MovementState.RUNNING_PRE_RECORDING_COUNTDOWN)) {
      // Only shrink if NOT transitioning to recording (countdown→recording stays fullscreen)
      if (enter !== enterKey(MovementState.RECORDING_MOVEMENT_VIDEO)) {
        actions.hideCountdown?.();
      }
    }

    if (exit === exitKey(MovementState.RECORDING_MOVEMENT_VIDEO)) {
      actions.stopMultiPersonDetection?.();
      actions.hideCountdown?.();
    }

    // ── ENTER EFFECTS ──

    // SHOWING_WELCOME_INSTRUCTION
    if (enter === enterKey(MovementState.SHOWING_WELCOME_INSTRUCTION)) {
      // IMPORTANT: must match CONSTANT.GENERAL_AUDIOS_KEY_TEXT values, not the JS property names
      const welcomeKey = CONSTANT.GENERAL_AUDIOS_KEY_TEXT.WELCOME_TEXT;
      console.log('[AUDIO] playing welcome audio:', welcomeKey);
      actions.playGeneralAudio?.(welcomeKey);
    }

    // SHOWING_MOVEMENT_INSTRUCTION
    if (enter === enterKey(MovementState.SHOWING_MOVEMENT_INSTRUCTION)) {
      const thisGeneration = ++generationRef.current;
      // Examinetics: wait silently for user to dismiss the modal
      if (context.isExamineticsEnabled) {
        // Audio is now handled entirely within ExamineticsInstructionModal.jsx
      } else {
        console.log('[FSM] SHOWING_MOVEMENT_INSTRUCTION — waiting silently for person in box');
        pendingAudioRef.current = null;
      }
    }

    // WAITING_FOR_PERSON_IN_BOX (non-Examinetics only)
    if (enter === enterKey(MovementState.WAITING_FOR_PERSON_IN_BOX)) {
      const thisGeneration = ++generationRef.current;
      const playAndTransition = async () => {
        const audioUrl = context.selectedExercise?.ExerciseGuideline?.AudioUrlWeb;
        console.log('[AUDIO] person in box, playing movement instruction audio:', audioUrl || 'none');
        if (audioUrl && actions.isVoiceEnabled?.()) {
          await actions.playAudioWithRetry?.(audioUrl, { priorityLevel: 2 });
        }
        if (cancelledRef.current || generationRef.current !== thisGeneration) return;
        actions.onInstructionAudioComplete?.();
        send(MovementEvent.INSTRUCTION_AUDIO_COMPLETE);
      };
      pendingAudioRef.current = playAndTransition;
      playAndTransition();
    }

    // WAITING_FOR_USER_VALIDATION
    if (enter === enterKey(MovementState.WAITING_FOR_USER_VALIDATION)) {
      console.log('[DETECTION] entering WAITING_FOR_USER_VALIDATION — detection checks active');
      actions.enableFeedbackAudio?.();
      actions.enableFeedbackAudio?.();
      pendingAudioRef.current = null;
    }

    // USER_READY_FOR_PRE_RECORDING
    if (enter === enterKey(MovementState.USER_READY_FOR_PRE_RECORDING)) {
      actions.disableTopControls?.();
      const playAndTransition = async () => {
        const audioUrl = actions.getStartInstructionAudioUrl?.();
        if (audioUrl) {
          await actions.stopPlayingAudio?.();
          await actions.playAudioWithRetry?.(audioUrl, { priorityLevel: 2 });
        }
        // Wait for any higher-priority audio to finish
        const audioManager = actions.getAudioManager?.();
        if (audioManager) {
          while (audioManager.getCurrentPriorityLevel() > 0 && audioManager.getCurrentPriorityLevel() <= 2) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
        if (cancelledRef.current) return;
        send('PRE_RECORDING_AUDIO_DONE');
      };
      pendingAudioRef.current = playAndTransition;
      playAndTransition();
    }

    // RUNNING_PRE_RECORDING_COUNTDOWN
    if (enter === enterKey(MovementState.RUNNING_PRE_RECORDING_COUNTDOWN)) {
      actions.showCountdown?.();
    }

    // RECORDING_MOVEMENT_VIDEO
    if (enter === enterKey(MovementState.RECORDING_MOVEMENT_VIDEO)) {
      actions.startRecording?.(context.videoDuration);
      // Play starting movement audio (fire-and-forget)
      const startingAudioUrl = actions.getStartingMovementAudioUrl?.();
      if (startingAudioUrl) {
        actions.playAudioWithRetry?.(startingAudioUrl, { priorityLevel: 2 });
      }
    }

    // RECORDING_INTERRUPTED_BY_MULTIPLE_PERSON
    if (enter === enterKey(MovementState.RECORDING_INTERRUPTED_BY_MULTIPLE_PERSON)) {
      actions.stopRecording?.();
      actions.playMultiPersonWarning?.();
      actions.resetMovementLevelState?.();

      // Auto-transition to validation after brief delay
      const resetAndTransition = async () => {
        await new Promise(resolve => setTimeout(resolve, 1500));
        if (cancelledRef.current) return;
        send('MOVEMENT_RESET');
      };
      resetAndTransition();
    }

    // MOVEMENT_RECORDING_COMPLETED
    if (enter === enterKey(MovementState.MOVEMENT_RECORDING_COMPLETED)) {
      actions.stopRecording?.();
      actions.processRecordingResult?.();
    }

  }, [currentState, prevState, context, actions, send]);
}
